import { useState } from 'react';
import { api } from '../lib/api.js';
import { Field, Banner } from '../components/ui.jsx';
import { Entrance } from './Entrance.jsx';

const MESSAGES = {
  wrong_password: 'That is not the password you signed in with.',
  password_too_short: 'Ten characters at least. A short sentence you will remember beats a clever short word.',
  password_contains_username: 'It cannot contain your username.',
  same_password: 'That is the password you already have.'
};

/**
 * The wall a handed over account hits on first sign in. Until this is done the API refuses
 * everything else, so the person who onboarded you stops knowing your password the moment
 * you use it. It wears the entrance layout when forced, and sits in a plain panel when
 * somebody changes their own password from inside.
 */
export function ChangePassword({ user, forced, onDone, onCancel, onSignOut }) {
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [again, setAgain] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);

  const mismatch = again.length > 0 && next !== again;
  const ready = current && next.length >= 10 && next === again && !busy;

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await api.changePassword(current, next);
      onDone();
    } catch (err) {
      setError(MESSAGES[err.data?.error] || 'That did not work. Try again.');
      setBusy(false);
    }
  }

  const form = (
    <form onSubmit={submit} style={{ display: 'grid', gap: 20 }}>
      <h1>{forced ? 'Set your own password' : 'Change your password'}</h1>

      {forced ? (
        <div className="lede">
          Welcome, {user.name}. You signed in with a temporary password somebody else can
          see. Replace it now and it becomes yours alone. Nothing else in EPMS opens until
          you do.
        </div>
      ) : (
        <div className="lede">This signs you out of every other device, because they are all still holding the old one.</div>
      )}

      <Field id="current" label={forced ? 'Temporary password' : 'Current password'} type="password"
        value={current} autoComplete="current-password" autoFocus
        onChange={e => setCurrent(e.target.value)} />

      <Field id="next" label="New password" type="password" value={next} autoComplete="new-password"
        hint="Ten characters at least. A short sentence works well and is easier to remember."
        onChange={e => setNext(e.target.value)} />

      <Field id="again" label="New password again" type="password" value={again} autoComplete="new-password"
        hint={mismatch ? 'These two do not match yet.' : undefined}
        onChange={e => setAgain(e.target.value)} />

      <button className="btn-ezel" type="submit" disabled={!ready}>
        {busy ? 'Saving' : 'Save and continue'}
      </button>

      {error && <Banner tone="danger">{error}</Banner>}

      {forced
        ? <button type="button" className="btn-ezel-quiet" onClick={onSignOut}>Sign out instead</button>
        : <button type="button" className="btn-ezel-quiet" onClick={onCancel}>Cancel</button>}
    </form>
  );

  if (forced) return <Entrance>{form}</Entrance>;

  return (
    <div style={{ minHeight: '100%', display: 'grid', placeItems: 'center', padding: 24 }}>
      <div className="entrance-card" style={{
        background: 'var(--ezel-offwhite)', padding: 32, borderRadius: 'var(--radius-lg)',
        boxShadow: 'var(--elevation-lg)', maxWidth: 440
      }}>
        {form}
      </div>
    </div>
  );
}
